import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ArrowLeft, Plus, FileText, Clock, CheckCircle, XCircle, AlertCircle, MessageSquare, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { LogoFull } from '@/components/Logo';

const STATUS_CONFIG = {
  open: { color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  in_review: { color: 'bg-blue-100 text-blue-700', icon: Clock },
  approved: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  denied: { color: 'bg-red-100 text-red-700', icon: XCircle },
  closed: { color: 'bg-gray-100 text-gray-700', icon: FileText },
};

const ClaimsManager = () => {
  const { user } = useAuth();
  const isAdmin = ['master_admin', 'carrier_admin', 'mga_admin'].includes(user?.role);
  
  const [claims, setClaims] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [noteText, setNoteText] = useState('');
  const [formData, setFormData] = useState({ policy_id: '', title: '', description: '', claim_type: 'general', incident_date: '', estimated_amount: '', priority: 'medium' });

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      const [claimsData, policiesData] = await Promise.all([
        api.getClaims(statusFilter || null),
        api.getPolicies()
      ]);
      setClaims(claimsData);
      setPolicies(policiesData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const claim = await api.createClaim({ ...formData, estimated_amount: parseFloat(formData.estimated_amount) || 0 });
      setClaims(prev => [claim, ...prev]);
      setShowForm(false);
      setFormData({ policy_id: '', title: '', description: '', claim_type: 'general', incident_date: '', estimated_amount: '', priority: 'medium' });
      toast.success('Claim created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create claim');
    }
  };

  const handleStatusUpdate = async (claimId, newStatus) => {
    try {
      const updated = await api.updateClaim(claimId, { status: newStatus });
      setClaims(prev => prev.map(c => c.claim_id === claimId ? updated : c));
      if (selectedClaim?.claim_id === claimId) setSelectedClaim(updated);
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim() || !selectedClaim) return;
    try {
      await api.addClaimNote(selectedClaim.claim_id, noteText);
      const refreshed = await api.getClaim(selectedClaim.claim_id);
      setSelectedClaim(refreshed);
      setClaims(prev => prev.map(c => c.claim_id === refreshed.claim_id ? refreshed : c));
      setNoteText('');
      toast.success('Note added');
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="claims-manager-page">
      <header className="bg-white border-b border-[#DCE4ED]">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <Link to={isAdmin ? '/admin' : '/builder'} className="flex items-center gap-2 text-[#8DA0B9] hover:text-[#0D1B2A] transition-colors">
              <ArrowLeft size={18} /><span className="text-sm">Back</span>
            </Link>
            <div className="flex items-center gap-2">
              <LogoFull size="small" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Claims Management</h1>
            <Button onClick={() => setShowForm(true)} className="btn-primary" data-testid="new-claim-btn"><Plus size={16} className="mr-2" />New Claim</Button>
          </div>
          <div className="flex gap-2 mt-4">
            {['', 'open', 'in_review', 'approved', 'denied', 'closed'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${statusFilter === s ? 'bg-[#1A3A64] text-white border-[#1A3A64]' : 'bg-white text-[#3D5A80] border-[#DCE4ED]'}`} data-testid={`filter-${s || 'all'}`}>
                {s ? s.replace('_', ' ') : 'All'}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Claims List */}
          <div className="flex-1 space-y-3">
            <AnimatePresence>
              {showForm && (
                <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} onSubmit={handleCreate} className="bg-white rounded-xl border border-[#DCE4ED] p-5 space-y-4 overflow-hidden">
                  <h3 className="font-medium text-[#0D1B2A]">File New Claim</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#3D5A80] block mb-1">Policy</label>
                      <select value={formData.policy_id} onChange={e => setFormData(p => ({...p, policy_id: e.target.value}))} className="w-full border border-[#DCE4ED] rounded-md px-3 py-2 text-sm" required data-testid="claim-policy-select">
                        <option value="">Select policy...</option>
                        {policies.map(p => <option key={p.policy_id} value={p.policy_id}>{p.name} ({p.policy_id})</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-[#3D5A80] block mb-1">Type</label>
                      <select value={formData.claim_type} onChange={e => setFormData(p => ({...p, claim_type: e.target.value}))} className="w-full border border-[#DCE4ED] rounded-md px-3 py-2 text-sm" data-testid="claim-type-select">
                        {['general', 'property_damage', 'liability', 'cyber_incident', 'workers_comp', 'auto_accident'].map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                      </select>
                    </div>
                  </div>
                  <Input value={formData.title} onChange={e => setFormData(p => ({...p, title: e.target.value}))} placeholder="Claim title..." className="border-[#DCE4ED]" required data-testid="claim-title-input" />
                  <textarea value={formData.description} onChange={e => setFormData(p => ({...p, description: e.target.value}))} placeholder="Describe the incident..." className="w-full border border-[#DCE4ED] rounded-md px-3 py-2 text-sm min-h-[80px] resize-none" data-testid="claim-description-input" />
                  <div className="grid grid-cols-3 gap-4">
                    <Input type="date" value={formData.incident_date} onChange={e => setFormData(p => ({...p, incident_date: e.target.value}))} className="border-[#DCE4ED]" data-testid="claim-date-input" />
                    <Input type="number" value={formData.estimated_amount} onChange={e => setFormData(p => ({...p, estimated_amount: e.target.value}))} placeholder="Est. amount ($)" className="border-[#DCE4ED]" data-testid="claim-amount-input" />
                    <select value={formData.priority} onChange={e => setFormData(p => ({...p, priority: e.target.value}))} className="border border-[#DCE4ED] rounded-md px-3 py-2 text-sm" data-testid="claim-priority-select">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="btn-primary" data-testid="submit-claim-btn">Submit Claim</Button>
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>

            {loading ? (
              <div className="text-center py-12"><div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full mx-auto" /></div>
            ) : claims.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-[#DCE4ED]">
                <FileText size={40} className="text-[#DCE4ED] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0D1B2A] mb-2">No Claims</h3>
                <p className="text-[#8DA0B9]">File a new claim to get started.</p>
              </div>
            ) : (
              claims.map(claim => {
                const cfg = STATUS_CONFIG[claim.status] || STATUS_CONFIG.open;
                const StatusIcon = cfg.icon;
                return (
                  <motion.div key={claim.claim_id} layout onClick={() => setSelectedClaim(claim)} className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${selectedClaim?.claim_id === claim.claim_id ? 'border-[#1A3A64]' : 'border-[#DCE4ED]'}`} data-testid={`claim-${claim.claim_id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-[#0D1B2A]">{claim.title || claim.claim_id}</h4>
                        <p className="text-xs text-[#8DA0B9]">{claim.claim_type?.replace(/_/g, ' ')} &bull; {new Date(claim.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${cfg.color}`}>
                        <StatusIcon size={12} />{claim.status?.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-[#3D5A80] line-clamp-1">{claim.description}</p>
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#DCE4ED]">
                      <span className="text-xs text-[#8DA0B9]">Priority: <span className={`font-medium ${claim.priority === 'urgent' ? 'text-red-600' : claim.priority === 'high' ? 'text-amber-600' : 'text-[#3D5A80]'}`}>{claim.priority}</span></span>
                      <span className="text-sm font-medium text-[#1A3A64]">${(claim.estimated_amount || 0).toLocaleString()}</span>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Detail Panel */}
          {selectedClaim && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="w-[380px] bg-white rounded-xl border border-[#DCE4ED] p-5 self-start sticky top-6" data-testid="claim-detail-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-[#0D1B2A]">Claim Details</h3>
                <button onClick={() => setSelectedClaim(null)} className="text-[#8DA0B9] hover:text-[#0D1B2A]">&times;</button>
              </div>
              <div className="space-y-3 text-sm">
                <div><span className="text-[#8DA0B9]">ID:</span> <span className="font-mono text-[#0D1B2A]">{selectedClaim.claim_id}</span></div>
                <div><span className="text-[#8DA0B9]">Policy:</span> <span className="text-[#0D1B2A]">{selectedClaim.policy_id}</span></div>
                <div><span className="text-[#8DA0B9]">Type:</span> <span className="text-[#0D1B2A]">{selectedClaim.claim_type?.replace(/_/g, ' ')}</span></div>
                <div><span className="text-[#8DA0B9]">Amount:</span> <span className="font-medium text-[#1A3A64]">${(selectedClaim.estimated_amount || 0).toLocaleString()}</span></div>
                {selectedClaim.incident_date && <div><span className="text-[#8DA0B9]">Incident:</span> <span className="text-[#0D1B2A]">{selectedClaim.incident_date}</span></div>}
              </div>

              {/* Status Actions (admin) */}
              {isAdmin && (
                <div className="mt-4 pt-4 border-t border-[#DCE4ED]">
                  <p className="text-xs text-[#8DA0B9] mb-2">Update Status:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['open', 'in_review', 'approved', 'denied', 'closed'].map(s => (
                      <button key={s} onClick={() => handleStatusUpdate(selectedClaim.claim_id, s)} className={`px-2 py-1 rounded text-xs font-medium border transition-all ${selectedClaim.status === s ? 'bg-[#1A3A64] text-white border-[#1A3A64]' : 'bg-white text-[#3D5A80] border-[#DCE4ED] hover:border-[#1A3A64]'}`} data-testid={`set-status-${s}`}>
                        {s.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="mt-4 pt-4 border-t border-[#DCE4ED]">
                <p className="text-xs text-[#8DA0B9] mb-2 flex items-center gap-1"><Clock size={12} />Timeline</p>
                <div className="space-y-2 max-h-[150px] overflow-y-auto">
                  {selectedClaim.timeline?.map((t, i) => (
                    <div key={i} className="text-xs text-[#3D5A80] flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#1A3A64] mt-1.5 flex-shrink-0" />
                      <div><p>{t.action}</p><p className="text-[#8DA0B9]">{new Date(t.at).toLocaleString()}</p></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="mt-4 pt-4 border-t border-[#DCE4ED]">
                <p className="text-xs text-[#8DA0B9] mb-2 flex items-center gap-1"><MessageSquare size={12} />Notes ({selectedClaim.notes?.length || 0})</p>
                <div className="space-y-2 max-h-[120px] overflow-y-auto mb-3">
                  {selectedClaim.notes?.map(n => (
                    <div key={n.note_id} className="p-2 bg-[#F0F4F8] rounded text-xs">
                      <p className="text-[#0D1B2A]">{n.text}</p>
                      <p className="text-[#8DA0B9] mt-1">{n.author_name} &bull; {new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                <form onSubmit={handleAddNote} className="flex gap-2">
                  <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add note..." className="flex-1 text-sm border-[#DCE4ED]" data-testid="claim-note-input" />
                  <Button type="submit" size="icon" className="btn-primary rounded-md" disabled={!noteText.trim()} data-testid="add-note-btn"><Send size={14} /></Button>
                </form>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClaimsManager;
